import React from 'react';

interface DefaultIconProps {
  className?: string;
  size?: number;
}

/**
 * Ícono por defecto para estado vacío
 * 
 * DESCRIPCIÓN VISUAL:
 * - Un documento/página con líneas de texto
 * - Forma rectangular vertical (como una hoja de papel)
 * - Tres líneas horizontales de diferentes longitudes que representan texto
 * - Un círculo pequeño con un punto en el centro (como un botón de información o ayuda)
 * - Representa contenido vacío, documentos o información general
 * - Estilo minimalista, icono genérico para estados vacíos
 * - Útil cuando no hay un icono específico para la sección
 * 
 * Usa currentColor para adaptarse automáticamente al tema light/dark
 */
export const DefaultIcon: React.FC<DefaultIconProps> = ({
  className = '',
  size = 150,
}) => {
  return (
    <svg
      width={size}
      height={(size * 146) / 150}
      viewBox="0 0 150 146"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <g clipPath="url(#clip0_default)">
        <path
          d="M24.97 54.3866H81.0558C82.0266 54.3866 82.813 53.6006 82.813 52.6302C82.813 51.6598 82.0266 50.8738 81.0558 50.8738H24.97C23.9991 50.8738 23.2128 51.6598 23.2128 52.6302C23.2128 53.6006 23.9991 54.3866 24.97 54.3866Z"
          fill="currentColor"
        />
        <path
          d="M61.7001 72.0955H24.97C23.9991 72.0955 23.2128 72.8815 23.2128 73.8519C23.2128 74.8223 23.9991 75.6083 24.97 75.6083H61.7001C62.671 75.6083 63.4573 74.8223 63.4573 73.8519C63.4573 72.8815 62.671 72.0955 61.7001 72.0955Z"
          fill="currentColor"
        />
        <path
          d="M57.865 92.259H24.97C23.9991 92.259 23.2128 93.045 23.2128 94.0154C23.2128 94.9859 23.9991 95.7718 24.97 95.7718H57.8694C58.8402 95.7718 59.6266 94.9859 59.6266 94.0154C59.6266 93.045 58.8402 92.259 57.8694 92.259H57.865Z"
          fill="currentColor"
        />
        <path
          d="M149.486 138.163L126.756 115.444C132.076 109.696 135.332 102.011 135.332 93.5851C135.332 83.4901 130.666 74.4666 123.374 68.5519V34.583C123.374 33.9332 123.119 33.2965 122.645 32.8222L119.859 30.0383L93.8219 4.01281L91.0368 1.22891C90.5579 0.754679 89.9253 0.5 89.2795 0.5H8.98375C4.02841 0.5 0 4.52656 0 9.47523V136.52C0 141.473 4.02841 145.5 8.98375 145.5H114.39C119.341 145.5 123.374 141.473 123.374 136.52V118.614C123.668 118.377 123.954 118.14 124.235 117.889L147 140.648C147.347 140.99 147.795 141.162 148.243 141.162C148.691 141.162 149.143 140.99 149.486 140.648C150.171 139.959 150.171 138.848 149.486 138.163ZM118.199 33.4677H94.9114C92.4206 33.4677 90.391 31.4391 90.391 28.9494V5.67261L118.199 33.4677ZM119.859 136.52C119.859 139.537 117.404 141.987 114.39 141.987H8.98375C5.97013 141.987 3.51442 139.537 3.51442 136.52V9.47523C3.51442 6.46299 5.97013 4.01281 8.98375 4.01281H86.7887V28.9494C86.7887 33.4238 90.4349 37.0683 94.9114 37.0683H119.859V66.0666C114.97 63.0807 109.228 61.355 103.087 61.355C85.3082 61.355 70.842 75.8102 70.842 93.5851C70.842 111.36 85.3082 125.811 103.087 125.811C109.228 125.811 114.97 124.085 119.859 121.099V136.52ZM123.374 113.898C122.284 114.982 121.111 115.984 119.859 116.884C115.137 120.291 109.343 122.298 103.087 122.298C87.2456 122.298 74.3564 109.419 74.3564 93.5851C74.3564 77.7511 87.2456 64.8678 103.087 64.8678C109.343 64.8678 115.137 66.8745 119.859 70.2863C121.111 71.1865 122.284 72.1877 123.374 73.2722C128.588 78.4712 131.817 85.6593 131.817 93.5851C131.817 101.511 128.588 108.695 123.374 113.898Z"
          fill="currentColor"
        />
        <path
          d="M107.959 79.0112C104.067 76.3898 98.5445 77.5314 95.7154 81.1847C94.8763 82.2693 94.0197 83.7227 94.2174 85.1542C94.3667 86.2168 95.5704 87.016 96.5808 86.5549C96.9674 86.3793 97.2485 86.028 97.4418 85.6548C97.7713 85.0181 97.9339 84.3243 98.3204 83.7096C98.6719 83.1431 99.12 82.6338 99.6427 82.221C101.005 81.1408 102.863 80.7412 104.541 81.233C108.376 82.3703 109.316 87.6132 107.216 90.6474C106.395 91.8373 105.209 92.9439 104.11 93.8748C103.148 94.6915 101.993 95.3941 101.527 96.6323C101.207 97.4886 101.347 98.3536 101.343 99.2406C101.343 99.8597 101.33 100.483 101.33 101.102C101.33 102.073 102.116 102.859 103.087 102.859C104.058 102.859 104.844 102.073 104.844 101.102C104.844 100.057 104.844 99.0122 104.844 97.9672C104.844 97.5588 105.731 97.0451 106.026 96.8123C107.985 95.2667 110.129 93.2425 111.082 90.8889C112.721 86.8448 111.715 81.5404 107.959 79.0112Z"
          fill="currentColor"
        />
        <path
          d="M103.087 104.716C101.518 104.716 100.245 105.994 100.245 107.562C100.245 109.129 101.518 110.403 103.087 110.403C104.655 110.403 105.929 109.125 105.929 107.562C105.929 105.998 104.655 104.716 103.087 104.716Z"
          fill="currentColor"
        />
      </g>
      <defs>
        <clipPath id="clip0_default">
          <rect width="150" height="145" fill="white" transform="translate(0 0.5)" />
        </clipPath>
      </defs>
    </svg>
  );
};

